import { Box, Theme, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { Notifications, Add } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { AlertCard } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { NotificationConfigTable, NotificationConfig } from './NotificationConfigTable';
import { NotificationConfigModal } from './NotificationConfigModal';
import { GET_NOTIFICATION_CONFIGS } from './notification-configs-model.gql';
import { GetNotificationConfigs } from './__generated__/GetNotificationConfigs';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
      display: 'flex',
      justifyContent: 'space-between',
    },
    headingLeft: {
      flex: 1,
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
    addButton: {
      minWidth: '140px',
    },
  })
);

const AdminConsoleCommunicationNotifications = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<NotificationConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useCampfireQuery<GetNotificationConfigs, {}>(GET_NOTIFICATION_CONFIGS, {});

  const saveNotificationFetch = useCampfireFetch({
    defer: true,
    withAuth: true,
  });

  const deleteNotificationFetch = useCampfireFetch({
    defer: true,
    withAuth: true,
  });

  // Transform GraphQL data into NotificationConfig format for display
  const notificationConfigs: NotificationConfig[] = data
    ? data.vm.notificationConfigs.map((config) => ({
        notificationConfigId: config.notificationConfigId,
        notificationType: config.notificationType,
        notifyAdmins: config.notifyAdmins,
        notifyProgramManagers: config.notifyProgramManagers,
        notifyActivityLeaders: config.notifyActivityLeaders,
        notifySpecificManagers: config.notifySpecificManagers,
        customEmail: config.customEmail,
        isEnabled: config.isEnabled,
        managers: config.managers,
      }))
    : [];

  const handleAddNotification = () => {
    setEditingConfig(null);
    setModalOpen(true);
  };

  const handleEditNotification = (config: NotificationConfig) => {
    setEditingConfig(config);
    setModalOpen(true);
  };

  const handleSaveNotification = async (configData: any) => {
    setSaving(true);
    try {
      const response = await saveNotificationFetch.run({
        method: 'post',
        url: `${apiUrl}/vm/notification-configs/save`,
        data: configData,
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: editingConfig
            ? 'Notification configuration updated successfully'
            : 'Notification configuration created successfully',
          variant: 'success',
        });
        setModalOpen(false);
        setEditingConfig(null);
        refetch();
      } else {
        const errorMessage = response.data?.message || 'Failed to save notification configuration';
        console.error('Error saving notification config:', response.data);
        setSnackbar({
          open: true,
          message: errorMessage,
          variant: 'error',
        });
      }
    } catch (saveError) {
      console.error('Error saving notification config:', saveError);
      setSnackbar({
        open: true,
        message: 'Failed to save notification configuration',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNotification = async (configId: string) => {
    try {
      const response = await deleteNotificationFetch.run({
        method: 'post',
        url: `${apiUrl}/vm/notification-configs/delete`,
        data: { id: configId },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Notification configuration deleted successfully',
          variant: 'success',
        });
        refetch();
      } else {
        const errorMessage = response.data?.message || 'Failed to delete notification configuration';
        console.error('Error deleting notification config:', response.data);
        setSnackbar({
          open: true,
          message: errorMessage,
          variant: 'error',
        });
      }
    } catch (deleteError) {
      console.error('Error deleting notification config:', deleteError);
      setSnackbar({
        open: true,
        message: 'Failed to delete notification configuration',
        variant: 'error',
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingConfig(null);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box className={classes.headingLeft}>
          <Typography variant='h2' className={classes.headingTitle}>
            Notifications & Alerts
          </Typography>
          <Typography variant={'body2'}>
            {'Configure who gets notified for important events and activities in your organisation'}
          </Typography>
        </Box>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleAddNotification}
          startIcon={<Add />}
          className={classes.addButton}
        >
          Add Notification
        </TabletButton>
      </Box>

      <Box className={classes.contentContainer}>
        <Box>
          <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
            <Notifications style={{ fontSize: '14px', paddingRight: '5px' }} /> Event Notifications
          </Typography>
          <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', marginBottom: '1rem' }}>
            {
              'Configure automatic notifications for important events like new applications, roster changes, and activity updates.'
            }
          </Typography>

          <AlertCard variant='info' title='How do notifications work?'>
            <Typography className={classes.infoBody}>
              Event notifications help keep the right people informed when important things happen in your organisation.
              You can choose who gets notified for each type of event - all admins, all program managers, specific
              managers, or custom email addresses.
              <br />
              <br />
              Each notification type can be enabled or disabled, and you can configure different recipients for
              different types of events. This ensures the right people get the right information at the right time.
            </Typography>
          </AlertCard>

          <NotificationConfigTable
            notificationConfigs={notificationConfigs}
            onEdit={handleEditNotification}
            onDelete={handleDeleteNotification}
            loading={loading}
            error={error}
          />
        </Box>
      </Box>

      <NotificationConfigModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveNotification}
        editingConfig={editingConfig}
        loading={saving}
      />
    </Box>
  );
};

export { AdminConsoleCommunicationNotifications };
